'use client'

import { useTranslations } from 'next-intl'
import { format } from 'date-fns'
import dynamic from 'next/dynamic'
import { ComponentType, Ref, useEffect, useImperativeHandle, useState, type JSX } from 'react'
import { useForm, useWatch, Control, FieldValues, FormProvider, UseFormResetField, Controller } from 'react-hook-form'

import useUpdateEffect from '../../hooks/update-effect'
import { GetValueByPath, SetValueByPath, UnsetValueByPath } from '../../utils'
import ToastStore from '../../stores/toast-store'
import { Submit } from '@radix-ui/react-form'
import { Button, Flex } from '@radix-ui/themes'

import { StyledForm, SubmitButtonContainer } from '../styled'
import { TField } from '../types'
import {
    FileField,
    EditorField,
    SelectField,
    ImageBufferField,
    PasswordField,
    InputField,
    TextareaField,
    CheckboxField,
    DateRangeField,
    DateTimeField,
    ImageUploading,
    RadioField,
    SliderField,
    StreamingFileField,
} from './fields'
import { getSelectDefaultValue } from './fields/select-field'
import { uploadStreamingFile } from './fields/streaming-file-field'

export { default as ErrorRow } from './error-row'
export { RequiredStar, IsFieldRequired, IsFieldDisabled, FieldRow, FieldLabel, getFieldRules } from './field-row'
export { useFieldState } from './hooks'

// @hookform/devtools is a dev dependency: the import only exists in development builds
const DevTool: ComponentType<{ control: Control<any> }> =
    process.env.NODE_ENV === 'development'
        ? dynamic(() => import('@hookform/devtools').then(mod => mod.DevTool), { ssr: false })
        : () => null

export const hiddenFieldsFilter = (field: TField, values: FieldValues) => (
    field.type == 'hidden' || (typeof field.hidden === 'function' ? !field.hidden(values) : !field.hidden)
)

export const getFieldKey = (field: TField, index?: number) => {
    if (!('name' in field))
        return `${index}-${field.type}`

    // radio buttons of one group share the name, so the value is part of the key
    return field.type === 'radio' ? `${field.name}-${field.value}` : field.name
}

/** `transformName` flattens every dot of a nested name (`a.b.c` -> `a__b__c`). */
export const toFormFieldName = (name: string, transformName?: boolean) =>
    transformName ? name.replaceAll('.', '__') : name

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/** Value for `<input type="date">`: keeps date-only strings as they are, otherwise the local calendar day. */
const toDateInputValue = (value: unknown) => {
    if (typeof value === 'string' && DATE_ONLY.test(value))
        return value

    const date = value instanceof Date ? value : typeof value === 'string' ? new Date(value) : undefined
    return date && !isNaN(date.getTime()) ? format(date, 'yyyy-MM-dd') : undefined
}

/** Value for `<input type="datetime-local">` in local time. */
const toDateTimeInputValue = (value: unknown) => {
    const date = value instanceof Date ? value : typeof value === 'string' ? new Date(value) : undefined
    return date && !isNaN(date.getTime()) ? format(date, "yyyy-MM-dd'T'HH:mm:ss") : undefined
}

/** Value for `<input type="time">`: time strings are kept, dates give their local `HH:mm`. */
const toTimeInputValue = (value: unknown) => {
    if (value instanceof Date)
        return isNaN(value.getTime()) ? undefined : format(value, 'HH:mm')

    return typeof value === 'string' ? value : undefined
}

export type TFormRef = {
    resetField: UseFormResetField<any>
    resetFields: (fields: string[]) => void
    getValues: (field?: string | string[]) => any
    setValue: (field: string, value: any) => void
}

type TCreateFieldProps = {
    rowField: TField
    labelWidth?: string
    data?: { [key: string]: any }
    transformName?: boolean
}

export const CreateField = ({ rowField, labelWidth, data, transformName }: TCreateFieldProps) => {
    if (rowField.type == 'element')
        return rowField.renderElement ? rowField.renderElement(data) : null

    const fieldName = toFormFieldName(rowField.name, transformName)
    // `data` is nested, the flattened `fieldName` would not be found in it
    const fieldItem = data ? GetValueByPath(data, rowField.name) : undefined

    switch (rowField.type) {
        case 'hidden': {
            return (
                <Controller
                    name={fieldName}
                    render={({ field }) => <input {...field} value={field.value ?? ''} disabled hidden />}
                />
            )
        }
        case 'password': {
            return (
                <PasswordField rowField={rowField} fieldName={fieldName} labelWidth={labelWidth} />
            )
        }
        case 'number':
        case 'text': {
            return <InputField rowField={rowField} labelWidth={labelWidth} fieldName={fieldName} />
        }
        case 'textarea': {
            return (
                <TextareaField rowField={rowField} labelWidth={labelWidth} fieldName={fieldName} />
            )
        }
        case 'editor': {
            return <EditorField rowField={rowField} fieldName={fieldName} labelWidth={labelWidth} />
        }
        case 'checkbox': {
            return (
                <CheckboxField rowField={rowField} fieldName={fieldName} labelWidth={labelWidth} />
            )
        }
        case 'select': {
            return <SelectField fieldName={fieldName} labelWidth={labelWidth} rowField={rowField} />
        }
        case 'date-range': {
            return (
                <DateRangeField fieldName={fieldName} labelWidth={labelWidth} rowField={rowField} />
            )
        }
        case 'datetime-local':
        case 'date':
        case 'time': {
            return (
                <DateTimeField fieldName={fieldName} rowField={rowField} labelWidth={labelWidth} />
            )
        }
        case 'file': {
            return (
                <FileField
                    fieldName={fieldName}
                    fieldItem={fieldItem}
                    labelWidth={labelWidth}
                    rowField={rowField}
                />
            )
        }
        case 'streaming-file': {
            return (
                <StreamingFileField
                    fieldName={fieldName}
                    fieldItem={fieldItem}
                    labelWidth={labelWidth}
                    rowField={rowField}
                />
            )
        }
        case 'gallery': {
            return (
                <ImageUploading
                    fieldName={fieldName}
                    labelWidth={labelWidth}
                    rowField={rowField}
                    fieldItem={fieldItem}
                />
            )
        }
        case 'radio': {
            return <RadioField fieldName={fieldName} labelWidth={labelWidth} rowField={rowField} />
        }
        case 'image-buffer': {
            return (
                <ImageBufferField
                    fieldName={fieldName}
                    labelWidth={labelWidth}
                    rowField={rowField}
                />
            )
        }
        case 'slider': {
            return <SliderField fieldName={fieldName} labelWidth={labelWidth} rowField={rowField} />
        }
    }
}

type TProps<T extends FieldValues> = {
    ref?: Ref<TFormRef | null>
    /**
     * With `streaming-file` fields it has to return `{ id }` of the saved record, the files are
     * uploaded afterwards.
     */
    onSubmit?: (data: NoInfer<T>) => Promise<any> | any
    fields: TField<NoInfer<T>>[]
    /**
     * Read when the form mounts (and when the set of fields changes). To load different data into a
     * mounted form, change its `key`.
     */
    data?: FieldValues
    submitText?: string
    resetOnSubmit?: boolean
    labelWidth?: string
    footer?: (isSubmitting: boolean, isValid: boolean) => JSX.Element
    CustomCreateForm?: (props: any) => JSX.Element
    className?: string
    transformName?: boolean // remove dot from field name
    debug?: boolean
    shouldUnregister?: boolean
    disabled?: boolean
    onClose?: () => void
    closeText?: string
}

const Form = <T extends FieldValues = any>({
    ref,
    onSubmit,
    fields,
    data = {},
    submitText = undefined,
    resetOnSubmit = true,
    labelWidth,
    footer,
    CustomCreateForm = StyledForm,
    className,
    transformName = false,
    debug = false,
    shouldUnregister = false,
    disabled = false,
    onClose,
    closeText,
}: TProps<T>) => {
    const t = useTranslations('form')
    const tGeneral = useTranslations()
    if (submitText === undefined)
        submitText = t('submit')

    const generateDefaultValues = async () => {
        let newData = structuredClone(data)
        // Nested names are written by path so the form state holds one nested object, not a flat "a.b" key.
        const assign = (fieldName: string, value: unknown) => {
            if (transformName)
                newData[fieldName] = value
            else
                SetValueByPath(newData, fieldName, value)
        }

        for (const field of fields) {
            if (field.type == 'element') {
                if (field.getDefaultValues)
                    newData = { ...newData, ...field.getDefaultValues(newData) }

                continue
            }

            const originalName = field['name']
            const fieldName = toFormFieldName(originalName, transformName)
            const fieldItem = GetValueByPath(data, originalName)

            switch (field.type) {
                case 'select': {
                    assign(fieldName, await getSelectDefaultValue(originalName, field, newData))
                    break
                }
                case 'checkbox': {
                    // databases often deliver 0/1
                    const initialValue = field.formatDefault
                        ? field.formatDefault(fieldItem)
                        : typeof fieldItem === 'boolean'
                            ? fieldItem
                            : Boolean(fieldItem ?? field.default)

                    assign(fieldName, initialValue)
                    break
                }
                case 'date-range': {
                    const defaultValue = field.default ?? (await field.loadDefault?.())
                    assign(
                        fieldName,
                        fieldItem ??
                        (defaultValue !== undefined
                            ? defaultValue.map(date => format(date, 'dd.MM.yyyy'))
                            : undefined)
                    )
                    break
                }
                case 'datetime-local': {
                    const defaultValue = fieldItem ?? field.default ?? (await field.loadDefault?.())
                    assign(fieldName, toDateTimeInputValue(defaultValue))
                    break
                }
                case 'date': {
                    const defaultValue = fieldItem ?? field.default ?? (await field.loadDefault?.())
                    assign(fieldName, toDateInputValue(defaultValue))
                    break
                }
                case 'slider': {
                    assign(fieldName, fieldItem ?? field.min)
                    break
                }
                case 'time': {
                    const defaultValue = fieldItem ?? field.default ?? (await field.loadDefault?.())
                    assign(fieldName, toTimeInputValue(defaultValue))
                    break
                }
                case 'textarea':
                case 'number':
                case 'text': {
                    const defaultValue = fieldItem ?? field.default ?? (await field.loadDefault?.())
                    assign(fieldName, defaultValue ?? undefined)
                    break
                }
                case 'radio': {
                    // several radio fields share one name; only the data value or the checked one decides
                    if (fieldItem !== undefined)
                        assign(fieldName, fieldItem)
                    else if (field.checked)
                        assign(fieldName, field.value)

                    break
                }
                default:
                    // '', 0 and false from data are kept
                    assign(fieldName, 'default' in field ? (fieldItem ?? field['default']) : fieldItem)
                    break
            }
        }

        // with transformName the values live under the flattened keys only
        if (transformName) {
            for (const field of fields) {
                if (field.type != 'element' && field.name.includes('.'))
                    UnsetValueByPath(newData, field.name)
            }
        }

        return newData
    }

    const methods = useForm<FieldValues>({
        mode: 'onChange',
        shouldUnregister,
        disabled,
    })

    // Values are only needed for fields with a `hidden(values)` predicate. Watching the whole form
    // otherwise re-renders every field on every keystroke.
    const hasConditionalFields = fields.some(field => 'hidden' in field && typeof field.hidden === 'function')
    const values = useWatch({ control: methods.control, disabled: !hasConditionalFields }) ?? {}

    const generateFieldKeys = () =>
        fields.map((field, index) => (field.type != 'element' ? field.name : index.toString()))
    const fieldKeys_ = generateFieldKeys()
    const [fieldKeys, setFieldKeys] = useState(fieldKeys_)

    // Runs only when the set of fields changes; the initial defaults are applied by the reset below
    // (running both on mount called loadDefault / loadOptions twice).
    useUpdateEffect(() => {
        let destructor = false
        const handleChange = async () => {
            const defaults = await generateDefaultValues()
            if (destructor)
                return

            const values = methods.getValues()

            fieldKeys_.forEach((key, index) => {
                if (index >= fieldKeys.length || fieldKeys[index] != key) {
                    const name = toFormFieldName(key, transformName)
                    const defaultValue = GetValueByPath(defaults, name as any)
                    if (!GetValueByPath(values, name as any) && defaultValue)
                        methods.resetField(name, { defaultValue })
                }
            })

            setFieldKeys(fieldKeys_)
        }

        handleChange()

        return () => {
            destructor = true
        }
    }, [fieldKeys_.join('|')])

    useEffect(() => {
        let cancelled = false
        const names = fields.flatMap(field =>
            field.type == 'element' ? [] : [toFormFieldName(field.name, transformName)]
        )
        const before = names.map(name => methods.getValues(name))

        const generate = async () => {
            const defaults = await generateDefaultValues()
            if (cancelled)
                return

            // whatever the user changed while slow defaults (loadDefault) were loading is kept
            names.forEach((name, index) => {
                const value = methods.getValues(name)
                if (value !== before[index])
                    SetValueByPath(defaults, name, value)
            })

            methods.reset(defaults)
        }

        generate()

        return () => {
            cancelled = true
        }
    }, [methods.reset])

    useImperativeHandle(
        ref,
        () => ({
            resetField: (field: string, options) =>
                methods.resetField(toFormFieldName(field, transformName), options),
            resetFields: (fields: string[]) =>
                fields.forEach(field => methods.resetField(toFormFieldName(field, transformName))),
            getValues: (field?: string | string[]) => methods.getValues(field as any),
            setValue: (field: string, value: any) =>
                methods.setValue(toFormFieldName(field, transformName), value),
        }),
        [methods, transformName]
    )

    const { updateToast } = ToastStore

    const onSubmitInternal = async (value: any) => {
        try {
            const values: Record<string, any> = {}
            Object.keys(value).forEach(key => {
                if (![undefined, null].includes(value[key]))
                    values[key] = value[key]
            })

            const res = await onSubmit?.(values as T)
            const id = res?.['id']

            for (const field of fields) {
                if (field.type !== 'streaming-file')
                    continue

                // without a newly picked file the value is just the stored file name
                const file = GetValueByPath(values, toFormFieldName(field.name, transformName) as any)
                if (!(file instanceof File))
                    continue

                if (id == null)
                    throw new Error(`Cannot upload "${field.name}": onSubmit has to return the id of the saved record`)

                await uploadStreamingFile(field, file, String(id))
            }

            if (resetOnSubmit)
                methods.reset()
        }
        catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error)
            updateToast({
                // backend error codes ("error.xxx") are translation keys
                message: message.startsWith('error.') ? tGeneral(message as any) : message,
                type: 'warning',
            })
        }
    }

    return (
        <>
            <FormProvider {...methods}>
                <CustomCreateForm
                    onSubmit={methods.handleSubmit(onSubmitInternal)}
                    noValidate
                    className={className}
                >
                    <div className="form-fields">
                        {fields.map((field, index) =>
                            // the index in `fields` keeps the keys stable while fields above hide
                            hiddenFieldsFilter(field, values) && (
                                <CreateField
                                    key={getFieldKey(field, index)}
                                    rowField={field}
                                    labelWidth={labelWidth}
                                    data={data}
                                    transformName={transformName}
                                />
                            )
                        )}
                    </div>

                    {onSubmit !== undefined && (
                        <SubmitButtonContainer className="form-footer">
                            {footer ? (
                                footer?.(methods.formState.isSubmitting, methods.formState.isValid)
                            ) : onClose ? (
                                <Flex pt="5" gap="4" justify="end">
                                    {onClose && (
                                        <Button
                                            disabled={methods.formState.isSubmitting}
                                            type="button"
                                            color="red"
                                            variant="soft"
                                            onClick={onClose}
                                        >
                                            {closeText ?? tGeneral('close')}
                                        </Button>
                                    )}

                                    <Submit asChild>
                                        <Button
                                            disabled={methods.formState.isSubmitting || !methods.formState.isValid}
                                            color="cyan"
                                        >
                                            {submitText}
                                        </Button>
                                    </Submit>
                                </Flex>
                            ) : (
                                <Submit asChild>
                                    <Button disabled={methods.formState.isSubmitting || !methods.formState.isValid} color="cyan">
                                        {submitText}
                                    </Button>
                                </Submit>
                            )}
                        </SubmitButtonContainer>
                    )}
                </CustomCreateForm>
            </FormProvider>

            {debug && <DevTool control={methods.control} />}
        </>
    )
}

export default Form
